import { useConsentStore } from "../store/consentStore";

/**
 * Our own hosts, matched on the WHOLE hostname: the app's origin, streammapp.com and its subdomains (player, main,
 * ossrs, legal site …), the three OVH object-storage buckets file-stc returns (`AWS_S3_URL_RETURN` in deploy_with_k3s:
 * streammapp-dev / -staging / -prod `.s3.de.io.cloud.ovh.net`) and the legacy media CDN. The web runtime config has no
 * storage host of its own, so the list lives here (same list as Android `player/ExternalMedia.kt`).
 */
const FIRST_PARTY_HOSTS: readonly RegExp[] = [
  /^(?:[a-z0-9-]+\.)*streammapp\.com$/i,
  /^streammapp-(?:dev|staging|prod)\.s3\.de\.io\.cloud\.ovh\.net$/i,
  /^dw16ojaos3czx\.cloudfront\.net$/i,
];

/**
 * Our buckets on a shared host (path-style S3): the host alone would admit every other bucket there, so only URLs whose
 * first path segment is the bucket count. The legacy uploads live in `whoisit.media` (owner ruling).
 */
const FIRST_PARTY_BUCKETS: Readonly<Record<string, string>> = { "s3.eu-central-1.amazonaws.com": "whoisit.media" };

/** True for a URL that loads from us (relative, data:, blob:, same origin, or a first-party host). */
export function isFirstPartyUrl(src: string): boolean {
  let url: URL;
  try {
    url = new URL(src, window.location.href);
  } catch {
    return false;
  }
  if (url.protocol === "data:" || url.protocol === "blob:") return true;
  if (url.host === window.location.host) return true;
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  if (FIRST_PARTY_HOSTS.some((re) => re.test(url.hostname))) return true;
  // URL has already resolved dot segments, so `/whoisit.media/../other/x` is `/other/x` here.
  const bucket = FIRST_PARTY_BUCKETS[url.hostname.toLowerCase()];
  const segments = url.pathname.split("/").filter(Boolean);
  return bucket !== undefined && segments.length >= 2 && segments[0] === bucket;
}

/** True once this browser said "load videos and music automatically" (external_media ACCEPTED). */
export function useExternalMediaAllowed(): boolean {
  return useConsentStore((s) => s.device.external_media?.decision === "ACCEPTED");
}

/**
 * The image URL a screen may load: a third-party image (YouTube / SoundCloud thumbnails, link previews) only after
 * external_media is ACCEPTED on this browser; otherwise null, and the screen draws its neutral placeholder (no request).
 */
export function shownImage(src: string | null | undefined, externalAllowed: boolean): string | null {
  if (!src) return null;
  return externalAllowed || isFirstPartyUrl(src) ? src : null;
}
